import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PackageOpen } from 'lucide-react';
import { Button } from '@atoms/Button';
import { Card } from '@atoms/Card';
import { Badge } from '@atoms/Badge';
import { Spinner } from '@atoms/Spinner';
import { OrderChatPanel } from '@/components/messaging/OrderChatPanel';
import { OrderActionConfirmDialog, type OrderStatusAction } from '@molecules/OrderActionConfirmDialog';
import { OrderFiltersPanel } from '@molecules/OrderFiltersPanel';
import { orderService } from '@services/orderService';
import { messagingService } from '@services/messagingService';
import {
  applyOrderFilters,
  DEFAULT_ORDER_FILTERS,
  type OrderFilters,
} from '@utils/orderFilters';
import {
  formatCurrency,
  formatDate,
  ORDER_STATUS_LABELS,
  orderStatusBadgeVariant,
} from '@utils/formatters';
import { useOrderDetailStore } from '@store/uiStore';
import type { Order, OrderStatus } from '@/types';

interface PendingAction {
  order: Order;
  action: OrderStatusAction;
}

export function OrdersSection() {
  const queryClient = useQueryClient();
  const openOrderDetail = useOrderDetailStore((s) => s.openOrderDetail);
  const [openChatOrderId, setOpenChatOrderId] = useState<string | null>(null);
  const [filters, setFilters] = useState<OrderFilters>(DEFAULT_ORDER_FILTERS);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const lastCheckRef = useRef<string>(new Date().toISOString());

  const { data: unreadOrders } = useQuery({
    queryKey: ['messages', 'unread-orders'],
    queryFn: () => messagingService.getUnreadOrders(),
  });

  const unreadOrderMap = useMemo(
    () => new Map((unreadOrders ?? []).map((row) => [row.orderId, row.count])),
    [unreadOrders],
  );

  const { data: orders, isLoading } = useQuery({
    queryKey: ['wholesaler', 'orders'],
    queryFn: () => orderService.getOrders(),
    refetchInterval: 30000,
  });

  const { data: newOrders } = useQuery({
    queryKey: ['wholesaler', 'new-orders', lastCheckRef.current],
    queryFn: () => orderService.checkNewOrders(lastCheckRef.current),
    refetchInterval: 15000,
  });

  useEffect(() => {
    if (newOrders?.count) {
      queryClient.invalidateQueries({ queryKey: ['wholesaler', 'orders'] });
    }
  }, [newOrders?.count, queryClient]);

  const dismissNewOrders = () => {
    lastCheckRef.current = new Date().toISOString();
    queryClient.invalidateQueries({ queryKey: ['wholesaler', 'new-orders'] });
  };

  const statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: OrderStatus }) =>
      orderService.updateStatus(id, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['wholesaler', 'orders'] });
      queryClient.invalidateQueries({ queryKey: ['wholesaler', 'overview'] });
      setPendingAction(null);
    },
  });

  const filtered = useMemo(
    () => applyOrderFilters(orders ?? [], filters),
    [orders, filters],
  );

  const filteredTotal = useMemo(
    () => filtered.reduce((sum, order) => sum + Number(order.totalPrice), 0),
    [filtered],
  );

  const requestAction = (order: Order, action: OrderStatusAction) => {
    setPendingAction({ order, action });
  };

  const confirmAction = () => {
    if (!pendingAction) return;
    const statusMap: Record<OrderStatusAction, OrderStatus> = {
      approve: 'approved',
      complete: 'completed',
      cancel: 'cancelled',
    };
    statusMutation.mutate({
      id: pendingAction.order.id,
      status: statusMap[pendingAction.action],
    });
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Siparişler</h1>
        <p className="mt-1 text-sm text-slate-500">
          Gelen siparişleri filtreleyin, onaylayın ve yönetin
        </p>
      </div>

      {newOrders && newOrders.count > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-brand-200 bg-brand-50 px-4 py-3 text-sm text-brand-800">
          <span className="font-medium">{newOrders.count} yeni sipariş var</span>
          <Button size="sm" variant="secondary" onClick={dismissNewOrders}>
            Görüldü
          </Button>
        </div>
      )}

      <OrderFiltersPanel
        filters={filters}
        onChange={setFilters}
        orders={orders ?? []}
        filteredCount={filtered.length}
        filteredTotal={filteredTotal}
      />

      {!filtered.length ? (
        <Card className="flex flex-col items-center gap-3 py-12 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
            <PackageOpen className="h-6 w-6" />
          </div>
          <div>
            <p className="font-medium text-slate-700">Sipariş bulunamadı</p>
            <p className="mt-1 text-sm text-slate-500">Seçili filtrelere uygun sipariş yok</p>
          </div>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((order) => (
            <Card key={order.id} className="card-elevated">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-semibold text-slate-900">
                      {order.restaurant?.name ?? 'Restoran'}
                    </p>
                    <span className="text-xs text-slate-400">#{order.id.slice(0, 8).toUpperCase()}</span>
                  </div>
                  <p className="mt-1 text-sm text-slate-500">
                    {formatDate(order.createdAt)} ·{' '}
                    {order.orderType === 'delivery' ? 'Teslimat' : 'Gel-Al'}
                    {order.restaurant?.username ? ` · @${order.restaurant.username}` : ''}
                  </p>
                  {order.note && (
                    <p className="mt-2 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-600">
                      {order.note}
                    </p>
                  )}
                </div>
                <Badge variant={orderStatusBadgeVariant(order.status)}>
                  {ORDER_STATUS_LABELS[order.status]}
                </Badge>
              </div>

              <ul className="mt-4 space-y-1.5 text-sm">
                {order.items?.map((item) => (
                  <li key={item.id ?? item.productId} className="flex justify-between gap-3 text-slate-700">
                    <span className="truncate">
                      {item.productName} × {item.quantity}
                    </span>
                    <span className="shrink-0 font-medium">{formatCurrency(item.lineTotal)}</span>
                  </li>
                ))}
              </ul>

              <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 pt-4">
                <span className="text-lg font-bold text-brand-600">{formatCurrency(order.totalPrice)}</span>
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" onClick={() => openOrderDetail(order)}>
                    Detay
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={() =>
                      setOpenChatOrderId((prev) => (prev === order.id ? null : order.id))
                    }
                  >
                    Mesaj
                    {(unreadOrderMap.get(order.id) ?? 0) > 0 && (
                      <span className="ml-1 rounded-full bg-brand-500 px-1.5 py-0.5 text-[10px] font-bold text-white">
                        {unreadOrderMap.get(order.id)}
                      </span>
                    )}
                  </Button>
                  {order.status === 'pending' && (
                    <Button size="sm" onClick={() => requestAction(order, 'approve')}>
                      Onayla
                    </Button>
                  )}
                  {order.status === 'approved' && (
                    <Button size="sm" onClick={() => requestAction(order, 'complete')}>
                      Tamamla
                    </Button>
                  )}
                  {order.status !== 'cancelled' && order.status !== 'completed' && (
                    <Button size="sm" variant="danger" onClick={() => requestAction(order, 'cancel')}>
                      İptal
                    </Button>
                  )}
                </div>
              </div>

              {openChatOrderId === order.id && <OrderChatPanel order={order} />}
            </Card>
          ))}
        </div>
      )}

      <OrderActionConfirmDialog
        open={Boolean(pendingAction)}
        order={pendingAction?.order ?? null}
        action={pendingAction?.action ?? null}
        loading={statusMutation.isPending}
        onClose={() => setPendingAction(null)}
        onConfirm={confirmAction}
      />
    </div>
  );
}
